import { useMemo } from 'react';
import { useWidgetTokenStore } from '@elfsight/lib-utils';
import { useQuery } from '@tanstack/react-query';
import { useCustomMetadataFetchers } from '../../components/common';
import type { MetadataFetcherType } from './fetchers';
import { metadataFetchers } from './fetchers';

const MAX_RETRIES = 3;
const SHORT_REVALIDATION_TIMEOUT = 10_000;
const LONG_REVALIDATION_TIMEOUT = 30_000;

interface UseRadioMetadataProps {
  id?: string;
  enabled?: boolean;
  url?: string;
  fetcherType?: MetadataFetcherType;
  forceCORSProxy?: boolean;
  withShortRevalidation?: boolean;
}

export const useRadioMetadata = ({
  id,
  url,
  fetcherType,
  forceCORSProxy = false,
  enabled = true,
  withShortRevalidation = false
}: UseRadioMetadataProps) => {
  const widgetTokenStore = useWidgetTokenStore();

  const { customMetadataFetchers } = useCustomMetadataFetchers();
  const fetchMetadata = useMemo(() => {
    if (id && customMetadataFetchers?.[id]) {
      return customMetadataFetchers[id];
    }

    if (fetcherType) {
      return metadataFetchers[fetcherType];
    }

    return;
  }, [customMetadataFetchers, fetcherType, id]);

  const revalidationTimeout = withShortRevalidation
    ? SHORT_REVALIDATION_TIMEOUT
    : LONG_REVALIDATION_TIMEOUT;

  return useQuery({
    enabled: [enabled, fetchMetadata, url].every(Boolean),
    queryKey: ['radioMetadata', url, fetcherType, forceCORSProxy],
    queryFn: async () => {
      if (!fetchMetadata || !url) {
        return;
      }

      const metadata = await fetchMetadata({
        url,
        widgetToken: widgetTokenStore.get(),
        forceCORSProxy
      });
      const { data = '' } = metadata ?? {};

      return data;
    },
    retry: MAX_RETRIES,
    refetchInterval: revalidationTimeout,
    staleTime: revalidationTimeout
  });
};
